import type * as React from 'react'

import { cn } from '@/lib/utils'

function Table({
	className,
	containerClassName,
	...props
}: React.ComponentProps<'table'> & { containerClassName?: string }) {
	return (
		<div data-slot='table-container' className={cn('relative w-full overflow-x-auto', containerClassName)}>
			<table data-slot='table' className={cn('w-full caption-bottom text-body', className)} {...props} />
		</div>
	)
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
	return <thead data-slot='table-header' className={cn('bg-table-header-bg [&_tr]:border-b', className)} {...props} />
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
	return (
		<tbody data-slot='table-body' className={cn('bg-table-row-bg [&_tr:last-child]:border-0', className)} {...props} />
	)
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
	return (
		<tfoot
			data-slot='table-footer'
			className={cn('bg-muted/50 border-t font-medium [&>tr]:last:border-b-0', className)}
			{...props}
		/>
	)
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
	return (
		<tr
			data-slot='table-row'
			className={cn(
				'h-table-row bg-table-row-bg hover:bg-table-row-hover-bg data-[state=selected]:bg-table-row-selected-bg data-[state=selected]:hover:bg-table-row-selected-hover-bg border-b transition-colors',
				className,
			)}
			{...props}
		/>
	)
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
	return (
		<th
			data-slot='table-head'
			className={cn(
				'bg-table-header-bg text-foreground h-table-header px-2 text-left align-middle text-body font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
				className,
			)}
			{...props}
		/>
	)
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
	return (
		<td
			data-slot='table-cell'
			className={cn(
				'h-table-row p-2 align-middle text-body whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
				className,
			)}
			{...props}
		/>
	)
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
	return (
		<caption data-slot='table-caption' className={cn('text-muted-foreground mt-4 text-body', className)} {...props} />
	)
}

export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow }
